/**
 * State for the WebGL field.
 *
 * A module singleton rather than React state on purpose: these values change
 * every frame, and none of them should cause a render. anime.js tweens this
 * plain object; the canvas's render loop uploads whatever it currently holds.
 * Because the tween target is an object and not a DOM node, it can never
 * collide with a GSAP tween on an element.
 */
import { animate, utils, prefersReducedMotion } from "./anime";

export const field = {
  /* render loop */
  time: 0,
  mx: 0.5,
  my: 0.42,
  targetX: 0.5,
  targetY: 0.42,

  /* mood. Bloom is held well below full: this is the ground under readable
     type, not a hero backdrop, and the chapter's copy has to win. */
  warp: 1.0,
  hue: 0.0,
  bloom: 0.52,
  lift: -0.12,

  /* raised once, on mount */
  opacity: 1.0,

  /* false once the plates cover the field completely — the loop then idles */
  visible: true,
};

/**
 * The field is one continuous place, so these are moods of the same weather
 * rather than separate backgrounds.
 */
export const FIELD_STATES = {
  /* the long work chapter — quiet, so the specimens read */
  work:    { warp: 1.00, hue: 0.00, bloom: 0.52, lift: -0.12 },
  /* the closing chapter — warmer and a little brighter, less type over it */
  contact: { warp: 1.20, hue: 0.70, bloom: 0.72, lift: 0.02 },
};

/** Blend the field between two named moods. t is 0..1. */
export const mixFieldState = (fromName, toName, t) => {
  const a = FIELD_STATES[fromName];
  const b = FIELD_STATES[toName];
  if (!a || !b) return;
  field.warp = utils.lerp(a.warp, b.warp, t);
  field.hue = utils.lerp(a.hue, b.hue, t);
  field.bloom = utils.lerp(a.bloom, b.bloom, t);
  field.lift = utils.lerp(a.lift, b.lift, t);
};

export const setFieldState = (name, { duration = 1400 } = {}) => {
  const next = FIELD_STATES[name];
  if (!next) return;
  if (prefersReducedMotion()) {
    Object.assign(field, next);
    return;
  }
  animate(field, { ...next, duration, ease: "inOutQuad" });
};
